import React, { useEffect, useState } from "react";
import { Box, Text, Button, Notification, Heading, Anchor } from "grommet";
import axios from "axios";
import API_URL from "../../config/config"; // Adjust the import as needed
import { useNavigate, useSearchParams } from "react-router-dom";

const HandleAssessmentConfirmation = ({
  managerName,
  user_id,
  assessment_id,
  manager_id,
}) => {
  const navigate = useNavigate();
  const [inviteStatus, setInviteStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const inviteStatusFromEmail = searchParams.get("invite_status");
  const [isAutoRedirecting, setIsAutoRedirecting] = useState(false);

  useEffect(() => {
    if (inviteStatusFromEmail === "2") {
      setIsAutoRedirecting(true);
      handleAcceptAssessment();
    } else if (inviteStatusFromEmail === "-1") {
      setIsAutoRedirecting(true);
      handleRejectAssessment();
    }
  }, [inviteStatusFromEmail]);

  useEffect(() => {
    console.log("Manager details:", managerName);
    // Additional logic if needed
  }, [managerName]);

  // When inviteStatus is set (accepted or declined), show a success notification and navigate away after 2 seconds.
  useEffect(() => {
    if (inviteStatus === -1 || inviteStatus === 2) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        navigate("/");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [inviteStatus, navigate]);

  // Handler for accepting the assessment (invite_status: 1, sets is_complete to 2)
  const handleAcceptAssessment = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/user/update_assessment_status`,
        {
          user_id,
          assessment_id,
          invite_status: 2,
          manager_id: manager_id,
        },
      );
      console.log("Invite accepted:", response.data);
      setInviteStatus(2);
      setIsLoading(false);
    } catch (error) {
      console.error("Error accepting invite:", error);
      setError(
        error.response?.data?.message || error.message || "An error occurred.",
      );
      setIsLoading(false);
    }
  };

  // Handler for rejecting the assessment (invite_status: 2, sets is_complete to -1)
  const handleRejectAssessment = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/user/update_assessment_status`,
        {
          user_id,
          assessment_id,
          invite_status: -1,
          manager_id: manager_id,
        },
      );
      console.log("Invite rejected:", response.data);
      setInviteStatus(-1);
      setIsLoading(false);
    } catch (error) {
      console.error("Error rejecting invite:", error);
      setError(
        error.response?.data?.message || error.message || "An error occurred.",
      );
      setIsLoading(false);
    }
  };

  return (
    <Box pad="medium" gap="small">
      <Heading level={1}>You have a new assessment!</Heading>
      {managerName != "unknown" ? (
        <Text size="large" margin={{ bottom: "medium" }}>
          Your manager {managerName} has created a new assessment for you in the
          HPE Digital Skill Advisor. Please click a button below to either accept
          or reject the assessment.
        </Text>
      ) : (
        <Text size="large" margin={{ bottom: "medium" }}>
          A new assessment has been created for you in HPE Digital Skill Advisor.
          Please click a button below to either accept or reject the assessment.
        </Text>
      )}
      {/* Buttons for Accepting / Rejecting the Assessment */}
      {!isAutoRedirecting && (
        <Box direction="row" gap="medium">
          <Button
            label="Accept"
            primary
            onClick={handleAcceptAssessment}
            disabled={isLoading}
          />
          <Button
            label="Reject"
            onClick={handleRejectAssessment}
            disabled={isLoading}
            color="status-critical"
          />
        </Box>
      )}

      {/* Conditional error rendering */}
      {error && error.includes("The invitation link has already been used") ? (
        <Box
          direction="row"
          gap="small"
          align="center"
          margin={{ top: "small" }}
        >
          <Text color="status-critical">
            The invitation link has already been used. Please proceed to the
            application and sign in to your account using your existing
            credentials.
          </Text>
          <Anchor
            label="Login here"
            href="https://sga.education.hpe.com/login"
            target="_blank"
            rel="noopener noreferrer"
            color="status-critical"
          />
        </Box>
      ) : (
        error && <Text color="status-critical">{error}</Text>
      )}

      {inviteStatus === -1 && (
        <Text margin={{ top: "small" }} color="status-critical">
          Assessment rejected.
        </Text>
      )}

      {showSuccess && (
        <Notification
          status="normal"
          message="Success! Redirecting you to the application..."
        />
      )}
    </Box>
  );
};

export default HandleAssessmentConfirmation;
